import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { authService } from "@/services/auth.service";
import { COLLECTION_KEYS } from "@/hooks/useCollections";
import {
  collectionReverseLookupKeysForItemKeys,
  collectionReverseLookupKeysForItems,
} from "@/hooks/collectionInvalidation";
import { stashListQueryKey } from "@/features/stash/stashQueryKeys";
import type { ChatOperation } from "./chat-model";

/** Only owner-confirmed receipts invalidate current library reads. Never infer a write from prose. */
export async function refreshChatLibrary(operations: ChatOperation[], accountId: string, client: QueryClient) {
  const current = () => authService.getCurrentUser()?.user_id === accountId;
  if (!current()) return false;
  const receipts = operations.filter(op => !["preview", "superseded"].includes(op.status));
  const outcomes = receipts
    .flatMap(op => op.outcomes || [])
    .filter(item => ["changed", "unchanged"].includes(item.status));
  const finished = receipts.filter(op => op.background && op.status !== "pending");
  const collections = new Set(
    [
      ...outcomes.flatMap(item => [item.collection_id, item.source_collection_id]),
      ...finished.flatMap(op => op.affected?.collection_ids || []),
    ].filter((id): id is string => !!id),
  );
  const tags = new Set(
    [...outcomes.map(item => item.tag_id), ...finished.flatMap(op => op.affected?.tag_ids || [])].filter(Boolean),
  );
  const entities = new Set(outcomes.flatMap(item => (item.entity_id ? [item.entity_id] : [])));
  const posts = new Set(outcomes.flatMap(item => (item.post_id ? [item.post_id] : [])));
  const keys: QueryKey[] = [];
  if (collections.size) {
    keys.push(COLLECTION_KEYS.personal(), ...[...collections].map(id => ["collections", "detail", id]));
    keys.push(
      ...collectionReverseLookupKeysForItemKeys(outcomes.map(item => item.key)),
      ...collectionReverseLookupKeysForItems(
        outcomes.flatMap(item => [
          ...(item.post_id ? [{ type: "stash", id: item.post_id }] : []),
          ...(item.entity_id ? [{ type: "entity", id: item.entity_id }] : []),
        ]),
      ),
    );
    if (finished.length) keys.push(["collections-for-stash"], ["collections-for-entity"]);
  }
  if (tags.size) keys.push(["user-tags"], ["tag-stats"], ["stash-tags"], ["collection-tags"], ["stashes-with-tags"]);
  keys.push(...[...posts].map(stashListQueryKey), ...[...entities].map(id => ["entity", id]));
  if (posts.size || entities.size || finished.length) keys.push(["stashes"], ["search"]);
  if (finished.some(op => op.action?.startsWith("visibility_"))) keys.push(["entity"]);
  if (!keys.length) return false;
  const unique = [...new Map(keys.map(key => [JSON.stringify(key), key])).values()];
  // Cancel pre-write reads before the ordinary cache owners revalidate.
  await Promise.all(unique.map(queryKey => client.cancelQueries({ queryKey })));
  if (!current()) return false;
  await Promise.all(unique.map(queryKey => client.invalidateQueries({ queryKey }, { throwOnError: true })));
  return true;
}
